// ServicePhotoViewer.jsx - Enhanced with flag reason system
import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import './ServicePhotoViewer.css';

function ServicePhotoViewer({ user, service, onBack, onFlag }) {
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flaggingPhoto, setFlaggingPhoto] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [flagAction, setFlagAction] = useState('retake'); // retake or more_info
  const [submittingFlag, setSubmittingFlag] = useState(false);

  const photoTypes = [
    { key: 'before', label: 'Before', icon: '🔴', description: 'Equipment on arrival' },
    { key: 'after', label: 'After', icon: '✅', description: 'After fix/service' },
    { key: 'parts', label: 'Parts', icon: '🔧', description: 'Spare parts used' },
    { key: 'serial', label: 'Serial', icon: '🔢', description: 'Serial number tag' }
  ];

  const hasPhoto = (key) => service.photos && service.photos[key];
  const isFlagged = (key) => service.photoFlags && service.photoFlags[key]?.flagged;
  const isVerified = (key) => service.photoFlags && service.photoFlags[key]?.verified;
  const getFlagInfo = (key) => service.photoFlags && service.photoFlags[key];

  const openFlagModal = (photoKey) => {
    setFlaggingPhoto(photoKey);
    setFlagReason('');
    setFlagAction('retake');
    setFlagModalOpen(true);
  };

  const closeFlagModal = () => {
    setFlagModalOpen(false);
    setFlaggingPhoto(null);
    setFlagReason('');
    setFlagAction('retake');
  };

  const submitFlag = async () => {
    if (!flagReason.trim()) {
      alert('Please provide a reason for flagging this photo');
      return;
    }

    setSubmittingFlag(true);

    try {
      const serviceRef = doc(db, 'serviceRecords', service.id);
      
      const flagData = {
        flagged: true,
        verified: false,
        reason: flagReason.trim(),
        action: flagAction,
        flaggedBy: user.name,
        flaggedByRole: user.role,
        flaggedAt: new Date().toISOString(),
        timestamp: serverTimestamp()
      };

      await updateDoc(serviceRef, {
        [`photoFlags.${flaggingPhoto}`]: flagData
      });

      // ✅ FIXED: the `service` prop still has the OLD photoFlags at this
      // point — React hasn't re-rendered with fresh data yet, and the
      // Firestore write we just did doesn't update it either. Passing the
      // stale `service` straight to onFlag meant notifyPhotoFlagged()
      // could never find the flag it needed and silently gave up —
      // meaning no notification was EVER actually sent to the technician,
      // even though this success alert always showed regardless.
      const updatedService = {
        ...service,
        photoFlags: {
          ...service.photoFlags,
          [flaggingPhoto]: flagData
        }
      };

      // Call parent onFlag callback for notification — awaited so the
      // notification write has actually finished before we move on.
      if (onFlag) {
        await onFlag(updatedService, flaggingPhoto);
      }

      alert(`✅ Photo flagged successfully!\n\nThe technician will be notified to ${flagAction === 'retake' ? 'retake the photo' : 'provide more information'}.`);
      
      closeFlagModal();
      
      // Refresh page to show updated flag
      window.location.reload();
      
    } catch (error) {
      console.error('Error flagging photo:', error);
      alert('❌ Failed to flag photo. Please try again.');
    } finally {
      setSubmittingFlag(false);
    }
  };

  const unflagPhoto = async (photoKey) => {
    if (!window.confirm('Are you sure you want to remove the flag from this photo?')) {
      return;
    }

    try {
      const serviceRef = doc(db, 'serviceRecords', service.id);
      
      await updateDoc(serviceRef, {
        [`photoFlags.${photoKey}`]: {
          flagged: false,
          verified: true,
          verifiedBy: user.name,
          verifiedAt: new Date().toISOString(),
          timestamp: serverTimestamp()
        }
      });

      alert('✅ Photo flag removed and marked as verified!');
      window.location.reload();
      
    } catch (error) {
      console.error('Error unflagging photo:', error);
      alert('❌ Failed to unflag photo. Please try again.');
    }
  };

  const downloadPhoto = (photoData, filename) => {
    const link = document.createElement('a');
    link.href = photoData;
    link.download = filename;
    link.click();
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleString('en-KE');
  };

  const formatFlagDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-KE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="service-photo-viewer">
      {/* Header */}
      <header className="photo-viewer-header">
        <button onClick={onBack} className="photo-viewer-back-btn">
          ← Back
        </button>
        <h1 className="photo-viewer-title">
          📸 Photos — Service #{service.orderNumber || service.id?.slice(-4)}
        </h1>
        <div className="photo-count-badge">
          {service.photoCount || 0} / 4 photos captured
        </div>
      </header>

      {/* Service Info */}
      <div className="service-info-card">
        <div className="service-info-header">
          <div className="service-order-number">
            Order #{service.orderNumber || service.id?.slice(-4)}
          </div>
          <div className="service-date">{formatDate(service.createdAt)}</div>
        </div>

        <div className="service-details">
          <div className="service-detail-item">
            <div className="detail-label">Outlet</div>
            <div className="detail-value">{service.outletName}</div>
          </div>
          <div className="service-detail-item">
            <div className="detail-label">Technician</div>
            <div className="detail-value">{service.technicianName}</div>
          </div>
          <div className="service-detail-item">
            <div className="detail-label">Equipment</div>
            <div className="detail-value">{service.serialNumber}</div>
          </div>
          <div className="service-detail-item">
            <div className="detail-label">Service Type</div>
            <div className="detail-value">{service.serviceType}</div>
          </div>
        </div>
      </div>

      {/* Photos Grid */}
      <div className="photos-grid">
        {photoTypes.map(type => {
          const photo = hasPhoto(type.key) ? service.photos[type.key] : null;
          const flagged = isFlagged(type.key);
          const verified = isVerified(type.key);
          const flagInfo = getFlagInfo(type.key);

          return (
            <div key={type.key} className={`photo-card ${photo ? 'has-photo' : ''} ${flagged ? 'flagged-card' : ''}`}>
              {/* Photo Header */}
              <div className="photo-header">
                <div className="photo-type-badge">
                  <span className="photo-type-icon">{type.icon}</span>
                  <span className="photo-type-label">{type.label}</span>
                </div>
                {flagged && (
                  <div className="photo-flag-status flagged">🚩 Flagged</div>
                )}
                {verified && !flagged && (
                  <div className="photo-flag-status verified">✅ Verified</div>
                )}
              </div>

              {/* Flag Details (if flagged) */}
              {flagged && flagInfo && (
                <div className="flag-details-banner">
                  <div className="flag-banner-header">
                    <span className="flag-banner-icon">🚩</span>
                    <span className="flag-banner-title">Photo Flagged</span>
                  </div>
                  <div className="flag-banner-reason">
                    <strong>Reason:</strong> {flagInfo.reason}
                  </div>
                  <div className="flag-banner-action">
                    <strong>Action Required:</strong> {flagInfo.action === 'retake' ? '📸 Retake Photo' : '📝 Provide More Info'}
                  </div>
                  <div className="flag-banner-meta">
                    <div className="flag-meta-item">
                      <span className="flag-meta-label">Flagged by:</span>
                      <span className="flag-meta-value">{flagInfo.flaggedBy}</span>
                    </div>
                    <div className="flag-meta-item">
                      <span className="flag-meta-label">When:</span>
                      <span className="flag-meta-value">{formatFlagDate(flagInfo.flaggedAt)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Photo Image */}
              <div className="photo-image-container">
                {photo ? (
                  <img
                    src={photo}
                    alt={`${type.label} photo`}
                    className="photo-image"
                    onClick={() => setLightboxPhoto(photo)}
                  />
                ) : (
                  <div className="photo-missing">
                    <div className="photo-missing-icon">📷</div>
                    <div className="photo-missing-text">No photo</div>
                  </div>
                )}
              </div>

              {/* Photo Actions */}
              {photo && (
                <div className="photo-actions">
                  <button
                    className="photo-action-btn btn-download"
                    onClick={() => downloadPhoto(photo, `${service.outletName}_${type.label}.jpg`)}
                    title="Download photo"
                  >
                    📥 Download
                  </button>
                  <button
                    className="photo-action-btn btn-zoom"
                    onClick={() => setLightboxPhoto(photo)}
                    title="View full size"
                  >
                    🔍 Zoom
                  </button>
                  {user && (user.role === 'supervisor' || user.role === 'admin' || user.role === 'super_admin') && (
                    <button
                      className={`photo-action-btn ${flagged ? 'btn-unflag' : 'btn-flag'}`}
                      onClick={() => flagged ? unflagPhoto(type.key) : openFlagModal(type.key)}
                      title={flagged ? 'Unflag photo' : 'Flag photo for review'}
                    >
                      {flagged ? '✅ Unflag' : '🚩 Flag'}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* No Photos State */}
      {(!service.photos || Object.keys(service.photos).length === 0) && (
        <div className="no-photos">
          <div className="no-photos-icon">📸</div>
          <div className="no-photos-text">No photos available</div>
          <div className="no-photos-subtext">
            The technician did not capture any photos for this service
          </div>
        </div>
      )}

      {/* Flag Modal */}
      {flagModalOpen && (
        <div className="flag-modal-overlay" onClick={closeFlagModal}>
          <div className="flag-modal" onClick={(e) => e.stopPropagation()}>
            <div className="flag-modal-header">
              <h2 className="flag-modal-title">🚩 Flag Photo for Review</h2>
              <button className="flag-modal-close" onClick={closeFlagModal}>✕</button>
            </div>

            <div className="flag-modal-body">
              <div className="flag-modal-info">
                <div className="flag-info-item">
                  <span className="flag-info-label">Photo Type:</span>
                  <span className="flag-info-value">
                    {photoTypes.find(t => t.key === flaggingPhoto)?.icon} {photoTypes.find(t => t.key === flaggingPhoto)?.label}
                  </span>
                </div>
                <div className="flag-info-item">
                  <span className="flag-info-label">Technician:</span>
                  <span className="flag-info-value">{service.technicianName}</span>
                </div>
              </div>

              <div className="flag-form-group">
                <label className="flag-form-label">
                  Action Required <span className="required">*</span>
                </label>
                <div className="flag-action-options">
                  <label className={`flag-action-option ${flagAction === 'retake' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="flagAction"
                      value="retake"
                      checked={flagAction === 'retake'}
                      onChange={(e) => setFlagAction(e.target.value)}
                    />
                    <div className="action-option-content">
                      <span className="action-option-icon">📸</span>
                      <div className="action-option-text">
                        <div className="action-option-title">Retake Photo</div>
                        <div className="action-option-desc">Photo quality or angle needs improvement</div>
                      </div>
                    </div>
                  </label>

                  <label className={`flag-action-option ${flagAction === 'more_info' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="flagAction"
                      value="more_info"
                      checked={flagAction === 'more_info'}
                      onChange={(e) => setFlagAction(e.target.value)}
                    />
                    <div className="action-option-content">
                      <span className="action-option-icon">📝</span>
                      <div className="action-option-text">
                        <div className="action-option-title">More Information</div>
                        <div className="action-option-desc">Need additional details or clarification</div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flag-form-group">
                <label className="flag-form-label">
                  Reason for Flagging <span className="required">*</span>
                </label>
                <textarea
                  className="flag-reason-textarea"
                  placeholder="Explain what's wrong with this photo and what needs to be fixed..."
                  value={flagReason}
                  onChange={(e) => setFlagReason(e.target.value)}
                  rows={4}
                  maxLength={500}
                />
                <div className="flag-char-count">{flagReason.length} / 500 characters</div>
              </div>

              <div className="flag-warning-box">
                <span className="flag-warning-icon">⚠️</span>
                <div className="flag-warning-text">
                  <strong>Note:</strong> The technician will be notified immediately and asked to {flagAction === 'retake' ? 'retake this photo' : 'provide more information'}.
                </div>
              </div>
            </div>

            <div className="flag-modal-footer">
              <button 
                className="flag-modal-btn btn-cancel" 
                onClick={closeFlagModal}
                disabled={submittingFlag}
              >
                Cancel
              </button>
              <button 
                className="flag-modal-btn btn-submit" 
                onClick={submitFlag}
                disabled={submittingFlag || !flagReason.trim()}
              >
                {submittingFlag ? '🔄 Submitting...' : '🚩 Flag Photo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxPhoto && (
        <div className="photo-lightbox" onClick={() => setLightboxPhoto(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxPhoto} alt="Full size" className="lightbox-image" />
            <button
              className="lightbox-close"
              onClick={() => setLightboxPhoto(null)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServicePhotoViewer;
